import MinimapDropdown from './MinimapDropdown.js';
import MinimapSideBySideButton from "./MinimapSideBySideButton.js";
import MinimapExpandButton from "./MinimapExpandButton.js";
import { locales } from './Minimap3DLocales.js';
import './Minimap3D.css'; // IMPORTANT!!

const av = Autodesk.Viewing;
const GlobalManagerMixin = av.GlobalManagerMixin;

const BIMWALK_EXT = 'Autodesk.BimWalk';

// int value to string, e.g. 50 => "50px" - to used for style assignments.
const toPixels = (val) => {
    return val.toString() + 'px';
};

// int value to string, e.g. 50 => "50%" - to used for style assignments.
const toPercent = (val) => {
  return val.toString() + "%";
};

export default class Minimap3DLayout {
    constructor(minimap3DExtension) {
        this.minimap3DExtension = minimap3DExtension;
        this.viewer = this.minimap3DExtension.viewer;
        this.options = minimap3DExtension.options;

        this.setGlobalManager(this.viewer.globalManager);

        minimap3DExtension.extendLocalization(locales);

        this.sideBySideOptions = this.options.sideBySideOptions || {};

        // By default, show header.
        this.showHeader = this.options.showHeader === undefined ? true : this.options.showHeader;

        this.update  = this.update.bind(this);

        // Map widget size
        this.mapWidgetWidth  = 300;
        this.mapWidgetHeight = 300;

        // Offset in pixels for the minimap's layout from its side (left of right).
        this.sideOffset = this.options.sideOffset !== undefined ? this.options.sideOffset : 10;

        // Map widget position
        this.mapWidgetTop = 0;

        if (this.sideBySideOptions.left2DView) {
            this.mapWidgetLeft = 0;
            this.mapWidgetRight = undefined;
        } else {
            this.mapWidgetLeft = undefined;
            this.mapWidgetRight = 0;
        }

        // Params for choosing mapWidget size based on canvas size
        this.relMapWidgetSizeLongEdge  = 0.25;  // Restrict to max 25% of the canvas along the longer axis with
        this.relMapWidgetSizeShortEdge = 0.37;  // Restrict to max 37% of the canvas along the shorter axis (more tolerance here, to keep map useful for widescreen)

        // Dropdown for levels, only instantiated when there are levels
        this.dropdown = null;

        // Toggle between..
        //  - Small map on top-right of the screen (false)
        //  - Large map that fills left half of the screen (true)
        this.sideBySideEnabled = this.sideBySideOptions.sideBySideDefault || false;

        // Indicates if layout is currently configured for side-by-side view
        // (always false when map is hidden, because viewer needs to be resized to full width then)
        this.sideBySide = false;

        // Indicates if layout is currently configured for expanded view (full viewer)
        this.expanded = false;

        // Percent of viewer width that we reserve for minimap in side-by-side mode.
        // E.g. 40 => Minimap gets 40%, Main canvas gets 60%
        this.sideBySideSplit = this.sideBySideOptions.sideBySideSplit || 40;

        this.init();
    }

    setMinimapVisible(visible) {
        if (this.visible === visible) {
            return;
        }

        this.visible = visible;

        // While switching sheets, mapWidget might be null
        if (!this.mapWidget)
            return;

        // Trigger css anim. Note that doing this synchronously would skip the animation.
        const _window = this.getWindow();
        _window.requestAnimationFrame(()=> {
            // Since the callback is async, we need to make sure the extension is still loaded when we get here (UI might be destroyed already).
            if (!this.mapWidgetRoot) {
                return;
            }

            if (visible) {
                this.mapWidgetRoot.classList.add('visible');
            } else {
                this.mapWidgetRoot.classList.remove('visible');
            }

            if (!this.options.enableAlwaysOn) {
                this.viewer.getExtension("Autodesk.ViewCubeUi", function(ext) {
                    ext.displayViewCube(!visible);
                    ext.displayHomeButton(!visible);
                });
             }
        });

        // Make sure that we reserve space for the minimap as soon as we start fade-in
        this.updateBimWalkInfoButtonPos();

        // Apply side-by-side layout immediately when starting to fade in. On fade-out, we
        // delay this until the fade-out transition is done (see transitionend-handler in initWidgets)
        if (visible) {
            this.updateSideBySide();
        }
    }

    initWidgets() {
        const _document = this.getDocument();

        // Optional: add extra css class
        const classPostfix = this.options.additionalWrapperCss ? ' ' + this.options.additionalWrapperCss : '';

        // Root div for the minimap. This is only used for the toolbar: Since it is left outside the Minimap bounds, it cannot be inside the mapWidget directly,
        // because the mapWidget uses overflow:hidden to implement scrolling.
        this.mapWidgetRoot = _document.createElement('div');
        this.mapWidgetRoot.style.width = toPixels(this.mapWidgetWidth);
        this.mapWidgetRoot.style.height = toPixels(this.mapWidgetHeight);
        this.mapWidgetRoot.className = `minimap3D ${classPostfix} `;
        this.mapWidgetRoot.classList.add('adsk-viewing-viewer');
        this.mapWidgetRoot.classList.add(this.viewer.theme);

        this.mapWidgetHeader = _document.createElement('div');
        this.mapWidgetHeader.className = 'minimap-header';
        this.mapWidgetHeader.classList.add('adsk-viewing-viewer');
        this.mapWidgetHeader.classList.add('adsk-control-group');
        this.mapWidgetHeader.classList.add(this.viewer.theme);

        if (this.showHeader) {
            this.mapWidgetRoot.appendChild(this.mapWidgetHeader);
        }

        // Div showing the minimap
        this.mapWidget = _document.createElement('div');
        this.mapWidget.className = 'minimapScrollArea';
        this.mapWidgetRoot.appendChild(this.mapWidget);

        if (this.showHeader) {
            if (this.options.onExpand) {
                this.expandButton = new MinimapExpandButton(this);
            }

            this.initDropdownIfNeeded();
            this.sideBySideButton = new MinimapSideBySideButton(this);
        }

        // hidden until level map is set
        this.setMinimapVisible(false);

        // Whenver the mapWidget finished fading in or out, we might need to re-adjust
        // the position of the annoying Info button of BimWalk to avoid overlapping.
        this.mapWidgetRoot.addEventListener('transitionend',  () => {
            this.updateBimWalkInfoButtonPos();
            this.updateSideBySide();
        });
    }

    // Dropdown might be initialized only later, after all other widgets were already initialized, since
    // the floorData might arrive only later.
    initDropdownIfNeeded() {
        if (!this.showHeader || this.dropdown) {
            return;
        }

        this.dropdown = new MinimapDropdown(this);

        // If the dropdown was added just now, make sure that the sideBySide button is added only after it (to the right).
        if (this.sideBySideButton) {
            this.sideBySideButton.dtor();
            this.sideBySideButton = new MinimapSideBySideButton(this);
        }
    }

    destroyWidgets() {
        if (this.widgetAnimation) {
            this.widgetAnimation.stop();
            this.widgetAnimation = null;
        }

        if (this.mapWidgetRoot.parentElement) {
            this.mapWidgetRoot.parentElement.removeChild(this.mapWidgetRoot);
        }

        this.mapWidgetRoot = null;
        this.mapWidget = null;
        this.mapWidgetHeader = null;

        if (this.dropdown) {
            this.dropdown.dtor();
            this.dropdown = null;
        }

        if (this.expandButton) {
            this.expandButton.dtor();
            this.expandButton = null;
        }

        if (this.sideBySideButton) {
            this.sideBySideButton.dtor();
            this.sideBySideButton = null;
        }
    }

    init() {
        this.initWidgets();

        this.initialViewerWidth = this.viewer.container.clientWidth;
        this.initialViewerHeight = this.viewer.container.clientHeight;

        this.resizeObserver = new ResizeObserver(() => {
            this.updateSideBySide(true);
        });

        this.resizeObserver.observe(this.viewer.clientContainer);

        // choose widget size based on current canvas size
        this.updateMapSize(true);

        this.viewer.clientContainer.appendChild(this.mapWidgetRoot);
    }

    destroy() {
        this.destroyWidgets();

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // Restore viewer to its default dimensions.
        this.restoreViewerContainerStyle();
    }

    getClientContainerWidth() {
        // In case clientContainer doesn't have a definite width, use the viewer's initial width.
        // This scenario can happen when the clientContainer div doesn't have style at all - and the viewer's width is independent of the window width.
        return this.viewer.clientContainer.clientWidth || this.initialViewerWidth;
    }

    getClientContainerHeight() {
        // In case clientContainer doesn't have a definite height, use the viewer's initial height.
        // This scenario can happen when the clientContainer div doesn't have style at all - and the viewer's height is independent of the window size.
        return this.viewer.clientContainer.clientHeight || this.initialViewerHeight;
    }

    updateToolbar() {
        this.sideBySideButton && this.sideBySideButton.update();
        this.expandButton && this.expandButton.update();
    }

    update() {
        this.initDropdownIfNeeded();

        if (this.minimap3DExtension.isEnabled()) {
            this.setMinimapVisible(true);
        } else {
            this.setMinimapVisible(false);
        }

        this.updateToolbar();
        this.updateSideBySide();
    }

    setWidgetSize(w, h, top, left, right, headerHeight) {
        this.mapWidgetWidth  = w;
        this.mapWidgetHeight = h;
        this.mapWidgetTop = top;
        this.mapWidgetLeft = left;
        this.mapWidgetRight = right;

        this.mapWidgetRoot.style.width  = toPixels(w);
        this.mapWidgetRoot.style.height = toPixels(h);
        this.mapWidgetRoot.style.top = toPixels(top);
        this.mapWidgetRoot.style.left = left !== undefined ? toPixels(left) : 'initial';
        this.mapWidgetRoot.style.right = right !== undefined ? toPixels(right) : 'initial';

        if (headerHeight !== undefined) {
            this.mapWidgetHeader.style.height = toPixels(headerHeight);
            this.mapWidgetHeader.style.opacity = toPercent((headerHeight / this.getInitialHeaderHeight()) * 100);
        }

        if (this.minimap3DExtension.mapViewer) {
            this.minimap3DExtension.mapViewer.resize();
        }

        this.update();
    }

    setViewerSize(width, height, left, right, top) {
        this.viewer.container.style.width = toPixels(width);
        this.viewer.container.style.height = toPixels(height);
        this.viewer.container.style.left = toPixels(left);
        this.viewer.container.style.right = toPixels(right);
        this.viewer.container.style.top = toPixels(top);

        // keep canvas/camera in-sync, because we changed container size
        this.viewer.resize();

        this.update();
    }

    // Update mapWidget size based on canvas size
    updateMapSize(immediate, duration) {
        let canvasWidth = this.getClientContainerWidth();
        let canvasHeight = this.getClientContainerHeight();

        let width, height, top, left, right, showHeader;

        if (this.expanded) {
            width = canvasWidth;
            height = canvasHeight;
            top = 0;

            if (this.sideBySideOptions.left2DView) {
                left = 0;
            } else {
                right = 0;
            }

            showHeader = false;
        } else if (this.sideBySide) {
            // Fill x percent of the parent widget (x = sideBySideSplit)
            if (this.verticalSideBySide) {
                width  = canvasWidth;
                height = canvasHeight * (this.sideBySideSplit / 100);
            } else {
                width  = canvasWidth * (this.sideBySideSplit / 100);
                height = canvasHeight;
            }

            top = 0;

            if (this.sideBySideOptions.left2DView) {
                left = 0;
            } else {
                right = 0;
            }

            showHeader = true;
        } else {
            // Default mode: Small map on top-right of the screen
            let longEdge = Math.max(canvasWidth, canvasHeight);
            let shortEdge = Math.min(canvasWidth, canvasHeight);
            const minimapWidthToHeightRatio = 0.66;
            // Restrict mapSize to not exceed a certain percentage of canvas width/height
            width  = Math.min(this.relMapWidgetSizeLongEdge * longEdge, this.relMapWidgetSizeShortEdge * shortEdge);
            height = width * minimapWidthToHeightRatio + this.mapWidgetHeader.clientHeight; // make the minimap ratio 2:3, and include the height for the header
            top = 10;

            if (this.sideBySideOptions.left2DView) {
                left = this.sideOffset;
            } else {
                right = this.sideOffset;
            }

            showHeader = true;
        }

        if (immediate && !this.widgetAnimation) {
            return this.setWidgetSize(width, height, top, left, right);
        } else {
            return this.animateWidgetSize(width, height, top, left, right, showHeader, duration);
        }
    }

    getInitialHeaderHeight() {
        if (!this.headerHeight) {
            this.headerHeight = this.mapWidgetHeader.clientHeight;
        }

        return this.headerHeight;
    }

    animateWidgetSize(targetWidth, targetHeight, targetTop, targetLeft, targetRight, showHeader = true, duration) {
        const startWidth = this.mapWidgetWidth;
        const startHeight = this.mapWidgetHeight;
        const startTop = this.mapWidgetTop;
        const startLeft = this.mapWidgetLeft;
        const startRight = this.mapWidgetRight;
        const startHeaderHeight = this.mapWidgetHeader.clientHeight;
        duration = duration || this.minimap3DExtension.resizeDuration;

        if (this.widgetAnimation) {
            this.widgetAnimation.stop();
            this.widgetAnimation = null;
        }

        const onTimer = (t) => {
            t = Autodesk.Viewing.Private.smootherStep(t);

            const width = Autodesk.Viewing.Private.lerp(startWidth, targetWidth, t);
            const height = Autodesk.Viewing.Private.lerp(startHeight, targetHeight, t);
            const top = Autodesk.Viewing.Private.lerp(startTop, targetTop, t);
            const left = targetLeft !== undefined ? Autodesk.Viewing.Private.lerp(startLeft || 0, targetLeft, t) : undefined ;
            const right = targetRight !== undefined ? Autodesk.Viewing.Private.lerp(startRight || 0, targetRight, t) : undefined;
            const headerHeight = Autodesk.Viewing.Private.lerp(startHeaderHeight, showHeader ? this.getInitialHeaderHeight() + ((10 - targetTop)*2) : 0, t);

            this.setWidgetSize(
                width,
                height,
                top,
                left,
                right,
                headerHeight,
            );
        };

        return new Promise(resolve => {
            const onAnimEnd = () => {
                this.widgetAnimation = null;
                resolve();
            };

            this.widgetAnimation = Autodesk.Viewing.Private.fadeValue(0, 1, duration, onTimer, onAnimEnd);
        });
    }

    // Relocate BimWalk info button to avoid overlap with Map widget
    updateBimWalkInfoButtonPos() {
        const bimWalk = this.viewer.getExtension(BIMWALK_EXT);
        const button  = bimWalk && bimWalk.getInfoButton();
        if (!button) {
            return;
        }

        // If the infor icon is hidden, we don't need all the mess. Hopefully this will get the default
        // state if others remove the info button as well
        if (bimWalk.options.disableBimWalkInfoIcon) {
            return;
        }


        // Only reserve space minimap is visible or about to fade in.
        const _window = this.getWindow();
        let style = _window.getComputedStyle(this.mapWidgetRoot);
        let visible = this.visible || style.opacity > 0;
        let reservedWidth = (visible ? this.mapWidgetWidth : 0);

        // Shifting the info button is not needed in side-by-side mode,
        // because the map widget is outside the viewer canvas in this case.
        if (this.sideBySide) {
            reservedWidth = 0;
        }

        // Includes space between InfoButton and MapWidget + Space between MapWidget and right canvas boundary
        const Margin = 20;
        const right = reservedWidth + Margin;
        button.style.right = toPixels(right);
    }

    setSideBySideEnabled(value) {
        this.sideBySideEnabled = value;
        this.updateSideBySide();
    }

    setSideBySideSplit(value) {
        this.sideBySideSplit = value;
        this.updateSideBySide(true);
        this.updateBimWalkInfoButtonPos();
    }

    animateViewerSize(targetWidth, targetHeight, targetLeft, targetRight, targetTop, duration) {
        const startWidth = this.viewer.container.clientWidth;
        const startHeight = this.viewer.container.clientHeight;
        const startLeft = this.viewer.container.clientLeft;
        const startRight = this.viewer.container.clientRight;
        const startTop = this.viewer.container.clientTop;

        if (this.viewerResizeAnimation) {
            this.viewerResizeAnimation.stop();
            this.viewerResizeAnimation = null;
        }

        duration = duration || this.minimap3DExtension.resizeDuration;

        const onTimer = (t) => {
            t = Autodesk.Viewing.Private.smootherStep(t);

            const width = Autodesk.Viewing.Private.lerp(startWidth, targetWidth, t);
            const height = Autodesk.Viewing.Private.lerp(startHeight, targetHeight, t);
            const top = Autodesk.Viewing.Private.lerp(startTop, targetTop, t);
            const left = Autodesk.Viewing.Private.lerp(startLeft, targetLeft, t);
            const right = Autodesk.Viewing.Private.lerp(startRight, targetRight, t);

            this.setViewerSize(
                width,
                height,
                left,
                right,
                top
            );
        };

        return new Promise(resolve => {
            const onAnimEnd = () => {
                this.viewerResizeAnimation = null;
                resolve();
            };

            this.viewerResizeAnimation = Autodesk.Viewing.Private.fadeValue(0, 1, duration, onTimer, onAnimEnd);
        });
    }

    // Make sure that side-by-side layout is activated if and only if minimap is visible
    updateSideBySide(force) {

        let wasSideBySide = this.sideBySide;
        this.sideBySide = this.visible && this.sideBySideEnabled;

        if (!force && wasSideBySide === this.sideBySide) {
            return;
        }

        let targetWidth, targetHeight, targetLeft, targetRight, targetTop;

        const clientHeight = this.getClientContainerHeight();
        const clientWidth = this.getClientContainerWidth();

        if (this.sideBySide) {
            this.mapWidgetRoot.classList.add('side-by-side');

            if (clientWidth >= clientHeight) {
                targetWidth = (100 - this.sideBySideSplit) / 100 * clientWidth;
                targetHeight = clientHeight;

                if (this.sideBySideOptions.left2DView) {
                    targetLeft = this.sideBySideSplit / 100 * clientWidth;
                    targetRight = 0;
                } else {
                    targetLeft = 0;
                    targetRight = this.sideBySideSplit / 100 * clientWidth;
                }

                targetTop = 0;

                if (this.sideBySideOptions.left2DView) {
                    this.mapWidgetRoot.classList.add('left-2d-view');
                }

                this.verticalSideBySide = false;
            } else {
                targetWidth = clientWidth;
                targetHeight = (100 - this.sideBySideSplit) / 100 * clientHeight;
                targetLeft = 0;
                targetRight = 0;
                targetTop = (this.sideBySideSplit / 100) * clientHeight;

                this.verticalSideBySide = true;
            }

            if (this.sideBySideOptions.onSideBySideUpdated) {
                this.sideBySideOptions.onSideBySideUpdated(this.sideBySideSplit);
            }
        } else {
            targetWidth = clientWidth;
            targetHeight = clientHeight;
            targetLeft = 0;
            targetRight = 0;
            targetTop = 0;

            this.mapWidgetRoot.classList.remove('side-by-side');
            this.mapWidgetRoot.classList.remove('left-2d-view');
        }

        if (force) {
            this.setViewerSize(targetWidth, targetHeight, targetLeft, targetRight, targetTop);
        } else {
            this.animateViewerSize(targetWidth, targetHeight, targetLeft, targetRight, targetTop);
        }

        this.updateMapSize(force);
    }

    // Restores default state of container for 3D Minimap and Design Diff Extensions
    restoreViewerContainerStyle() {
        if (this.viewerResizeAnimation) {
            this.viewerResizeAnimation.stop();
            this.viewerResizeAnimation = null;
        }

        this.viewer.container.style.width = "100%";
        this.viewer.container.style.height = "100%";
        this.viewer.container.style.left  = "";
        this.viewer.container.style.right  = "";
        this.viewer.container.style.top = "";

        // Update viewer size.
        this.viewer.resize();
    }

    getMapViewerContainer() {
        return this.mapWidget;
    }

    expandSheetMap(duration) {
        if (this.expanded) {
            return;
        }

        this.expanded = true;

        this.mapWidgetHeader.style.pointerEvents = 'none';

        this.setSideBySideEnabled(false);

        return this.updateMapSize(false, duration);
    }
}

GlobalManagerMixin.call(Minimap3DLayout.prototype);
